import { inject, Injectable } from '@angular/core';
import { AbstractService } from './abstract-service';
import { Theme, ThemeService } from './theme.service';
import { BrowserTools } from '../tools/browser-tools';

export interface EmbedState {
  /** Pinned by HostBridgeService once the host:init handshake completes. */
  parentOrigin: string | undefined;
  /** Monotonic counter for outbound child:navigate-host messages. */
  nextSeqId: number;
}

/**
 * Owns post-handshake parent-origin pinning, host-delegated navigation, and the
 * embedded-mode theme override.
 *
 * Whether the app is embedded is environment detection, not service state —
 * read it via `BrowserTools.isEmbedded()` instead. This service owns the
 * security boundary (pinned parentOrigin), the outbound navigateHost message,
 * and the asm_light theme override applied once at construction when embedded.
 */
@Injectable({
  providedIn: 'root',
})
export class EmbedService extends AbstractService<EmbedState> {
  private readonly themeService = inject(ThemeService);

  constructor() {
    super({ parentOrigin: undefined, nextSeqId: 1 });

    // When the app runs embedded inside the Vue host shell, force asm_light theme
    // so the Angular content is visually seamless with the Vue shell's light theme.
    // The override is NOT persisted — the user's saved preference remains intact.
    if (BrowserTools.isEmbedded()) {
      this.themeService.overrideTheme(Theme.ASM_LIGHT);
    }
  }

  public setParentOrigin(parentOrigin: string): void {
    this.patch({ parentOrigin });
  }

  /**
   * Ask the Vue host to navigate to a Vue-owned route.
   * No-op in standalone mode. Angular MUST NOT navigate window.top directly.
   *
   * @param hostPath  Absolute Vue path, e.g. "/domains" or "/"
   * @param hostQuery Optional query params to pass to the Vue route
   */
  public navigateHost(hostPath: string, hostQuery?: Record<string, string>): void {
    if (!BrowserTools.isEmbedded()) {
      return;
    }
    const parentOrigin = this.state.parentOrigin;
    if (!parentOrigin) {
      // We're inside an iframe but the host:init handshake hasn't completed yet —
      // surface as a developer hint so this race is visible during integration.
      console.warn('navigateHost called before host:init handshake completed');
      return;
    }
    const sequenceId = this.state.nextSeqId;
    this.patch({ nextSeqId: sequenceId + 1 });
    window.parent.postMessage(
      {
        type: 'child:navigate-host',
        sequenceId,
        payload: { hostPath, hostQuery },
      },
      parentOrigin,
    );
  }
}
